// /crisis/:id — Sala de Situación de una crisis.
//
// La pantalla que se mira varias veces por día mientras dura: qué está pasando,
// cuánto pegó, qué hay que hacer y quién lo está haciendo.
//
// Todo lo que se muestra acá sale de datos reales de la plataforma. Cuando un
// bloque no se puede calcular, se dice — porque en una crisis "0 notas hoy" es
// una gran noticia y "no pude contar las notas" no es ninguna.

import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line,
  XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine,
} from 'recharts';
import {
  FaArrowLeft, FaPlus, FaFolderOpen, FaRedo,
  FaNewspaper, FaBullhorn,
} from 'react-icons/fa';
import {
  TechShell, TechHeader, TechTitle, TechSubtitle, TechPanel, PanelHeader, PanelTitle,
  StatStrip, StatTile, StatLabel, StatValue, StatSub,
  TechInput, TechSelect, TechButton, TechButtonGhost,
  TechChip, TechBar, TechBarFill, Empty, ErrorBox, Loading, mono, borderSoft,
} from '../../styles/techComponents';
import {
  getPanelCrisis, refrescarCrisis, crearAccion, actualizarAccion, borrarAccion, cambiarEstadoCrisis,
} from '../../services/crisisService';
import AccionCrisis from './AccionCrisis';
import AccionModal from './AccionModal';
import NotasMedioModal from './NotasMedioModal';
import HumorSocial from './HumorSocial';
import KitFuncional from './KitFuncional';
import { useCrisis } from '../../contexts/CrisisContext';
import friendlyError from '../../utils/friendlyError';

const C = { alerta: '#ef476f', ok: '#06d6a0', dato: '#00b4d8', tibio: '#ffd166' };

const Volver = styled(Link)`
  display: inline-flex; align-items: center; gap: 7px;
  color: var(--text-secondary); text-decoration: none;
  font-size: 0.82rem; margin-bottom: 10px;
  &:hover { color: var(--text-primary); }
`;

const Resumen = styled.p`
  margin: 10px 0 0; color: var(--text-secondary);
  font-size: 0.92rem; line-height: 1.65; max-width: 90ch;
`;

const Desenlace = styled.div`
  margin: 0 0 20px;
  padding: 14px 16px;
  border: 1px solid ${(p) => (p.$ok ? 'rgba(6,214,160,0.4)' : 'rgba(0,180,216,0.4)')};
  border-left: 3px solid ${(p) => (p.$ok ? '#06d6a0' : 'var(--btn-primary)')};
  border-radius: 6px;
  background: ${(p) => (p.$ok ? 'rgba(6,214,160,0.07)' : 'rgba(0,180,216,0.06)')};
  .cab { font-size: 0.95rem; color: var(--text-primary); margin-bottom: 8px; }
  p { margin: 4px 0; font-size: 0.88rem; line-height: 1.6; color: var(--text-secondary); }
  .razon { font-size: 0.82rem; opacity: 0.85; }
`;

const Barra = styled.div` display: flex; gap: 8px; flex-wrap: wrap; margin-top: 14px; `;

const Grilla = styled.div`
  display: grid; gap: 0.9rem;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
`;

const Fila = styled.div`
  display: flex; align-items: flex-start; gap: 11px;
  padding: 10px 0; border-bottom: 1px solid ${borderSoft};
  &:last-child { border-bottom: none; }
`;

// Fila de medio: se puede tocar para filtrar las notas por ese medio.
const FilaClic = styled.div`
  display: flex; align-items: flex-start; gap: 11px;
  padding: 10px 8px; margin: 0 -8px;
  border-bottom: 1px solid ${borderSoft};
  cursor: pointer; border-radius: 5px;
  background: ${(p) => (p.$activa ? 'rgba(239, 71, 111, 0.08)' : 'transparent')};
  &:hover { background: ${(p) => (p.$activa ? 'rgba(239, 71, 111, 0.12)' : 'rgba(255,255,255,0.03)')}; }
  &:last-child { border-bottom: none; }
`;

const Texto = styled.div`
  flex: 1; min-width: 0;
  text-decoration: ${(p) => (p.$hecha ? 'line-through' : 'none')};
  opacity: ${(p) => (p.$hecha ? 0.55 : 1)};
`;

const Titulo = styled.div` font-size: 0.9rem; color: var(--text-primary); line-height: 1.45; `;
const Sub = styled.div` font-size: 0.76rem; color: var(--text-secondary); margin-top: 3px; `;

const Medio = styled.span`
  font-family: ${mono}; font-size: 0.68rem; color: var(--text-secondary);
`;

const Nota = styled.a`
  display: block; padding: 9px 0; border-bottom: 1px solid ${borderSoft};
  text-decoration: none; color: inherit;
  &:last-child { border-bottom: none; }
  &:hover .t { color: var(--btn-primary); }
`;

const Agregar = styled.div`
  display: flex; gap: 8px; margin-top: 12px; flex-wrap: wrap;
  > input { flex: 1; min-width: 200px; }
`;

const Leyenda = styled.p`
  margin: 0 0 10px; font-size: 0.78rem; color: var(--text-secondary); line-height: 1.5;
`;

// La frase que explica el gráfico. Va arriba y no abajo: primero qué pasa,
// después el dibujo — quien mira esto en una crisis no quiere interpretar ejes.
const Lectura = styled.p`
  margin: 0 0 12px;
  font-size: 0.88rem;
  line-height: 1.6;
  color: var(--text-primary);
  padding-left: 10px;
  border-left: 2px solid ${(p) => p.$color || 'var(--btn-primary)'};
`;

// Contadores chicos debajo del gráfico.
const Cifras = styled.div`
  display: flex; flex-wrap: wrap; gap: 18px;
  margin-top: 12px; padding-top: 12px;
  border-top: 1px solid ${borderSoft};
`;

const Cifra = styled.div`
  display: flex; flex-direction: column; gap: 2px;
  .n {
    font-family: ${mono}; font-size: 1.05rem; font-weight: 600;
    color: ${(p) => p.$color || 'var(--text-primary)'};
  }
  .r {
    font-size: 0.7rem; letter-spacing: 0.04em;
    color: var(--text-secondary); text-transform: uppercase;
  }
`;

// Cómo viene la cobertura, en una palabra y un color.
const FASE = {
  estallando: { texto: 'estallando', color: '#ef476f' },
  subiendo: { texto: 'creciendo', color: '#ef476f' },
  sostenida: { texto: 'sostenida', color: '#ffd166' },
  bajando: { texto: 'bajando', color: '#06d6a0' },
  apagada: { texto: 'apagada', color: '#06d6a0' },
  sin_datos: { texto: 'sin datos', color: 'var(--text-secondary)' },
};

const ejeStyle = { fontSize: 11, fill: 'rgba(255,255,255,0.45)' };
const tooltipStyle = {
  background: 'rgba(11,19,32,0.96)', border: '1px solid rgba(255,255,255,0.14)',
  borderRadius: 8, fontSize: 12,
};

const diaCorto = (f) => {
  const d = new Date(`${f}T12:00:00`);
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
};

const ETIQUETA_ESTADO = {
  detectada: 'sin confirmar', confirmada: 'en curso', contenida: 'contenida',
  resuelta: 'resuelta', falsa_alarma: 'falsa alarma',
};

export default function SalaDeSituacion() {
  const { id } = useParams();
  const { refrescar: refrescarBanner } = useCrisis();

  const [panel, setPanel] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const [nueva, setNueva] = useState('');
  const [prioridadNueva, setPrioridadNueva] = useState('media');
  // Tocar un medio abre un popup con sus notas.
  const [medioAbierto, setMedioAbierto] = useState(null);
  // Tocar una acción abre el popup con su paso a paso, contactos y chat.
  const [accionAbierta, setAccionAbierta] = useState(null);
  const [refrescando, setRefrescando] = useState(false);
  const [avisoRefresh, setAvisoRefresh] = useState(null);

  const cargar = useCallback(async () => {
    setError(null);
    try {
      setPanel(await getPanelCrisis(id));
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setCargando(false);
    }
  }, [id]);

  useEffect(() => { cargar(); }, [cargar]);

  const conBloqueo = async (fn) => {
    setOcupado(true);
    try { await fn(); await cargar(); }
    catch (e) { setError(friendlyError(e)); }
    finally { setOcupado(false); }
  };

  // Un toque cicla pendiente → en curso → hecha → pendiente.
  const ciclar = (a) => {
    const siguiente = a.estado === 'pendiente' ? 'en_curso'
      : a.estado === 'en_curso' ? 'hecha' : 'pendiente';
    return conBloqueo(() => actualizarAccion(id, a.id, { estado: siguiente }));
  };

  const agregar = () => {
    if (nueva.trim().length < 3) return;
    return conBloqueo(async () => {
      await crearAccion(id, { titulo: nueva.trim(), prioridad: prioridadNueva });
      setNueva('');
    });
  };

  const cambiarCrisis = (estado) => conBloqueo(async () => {
    await cambiarEstadoCrisis(id, estado);
    await refrescarBanner();
  });

  // Actualiza a pedido: busca prensa nueva del tema, escala y re-evalúa, y
  // recarga el panel con todo fresco.
  const actualizar = async () => {
    if (refrescando) return;
    setRefrescando(true);
    setAvisoRefresh(null);
    try {
      const r = await refrescarCrisis(id);
      await cargar();
      const prensa = r.notas_nuevas > 0
        ? `${r.notas_nuevas} nota(s) nueva(s) del tema.`
        : 'No había prensa nueva del tema.';
      setAvisoRefresh(
        `${prensa}${r.imagen_actualizando
          ? ' La imagen y los comentarios de redes se están actualizando en segundo plano (tarda un par de minutos) — recargá para verlos.'
          : ''}`,
      );
      setTimeout(() => setAvisoRefresh(null), 12000);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setRefrescando(false);
    }
  };

  if (cargando) return <Loading>Armando la sala de situación…</Loading>;
  if (error && !panel) return <ErrorBox>{error}</ErrorBox>;
  if (!panel) return <Empty>No encontramos esta situación.</Empty>;

  const { crisis, ventana, cobertura, reputacion, acciones = [], progreso, kit,
    medios_en_silencio: silencio = [], reclamos = [], evolucion = [], redes = [], degradado = [] } = panel;
  const resolucion = crisis.resolucion || null;
  const ultimoParte = evolucion.length ? evolucion[evolucion.length - 1].fecha : null;

  const fallo = (b) => degradado.includes(b);
  const notasDelMedio = (cobertura?.notas || [])
    .filter((n) => n.medio === medioAbierto);
  const abierta = ['detectada', 'confirmada', 'contenida'].includes(crisis.estado);
  const delta = reputacion?.delta;

  return (
    <TechShell>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Volver to="/crisis"><FaArrowLeft /> Volver a Modo Crisis</Volver>
        {abierta && (
          <TechButton onClick={actualizar} disabled={refrescando} title="Buscar prensa nueva del tema y re-evaluar">
            <FaRedo style={{ animation: refrescando ? 'girarR 1s linear infinite' : 'none' }} />
            {refrescando ? ' Actualizando…' : ' Actualizar ahora'}
          </TechButton>
        )}
      </div>
      <style>{'@keyframes girarR { to { transform: rotate(360deg); } }'}</style>

      {avisoRefresh && (
        <ErrorBox style={{
          borderColor: 'rgba(6,214,160,0.4)', color: 'var(--text-primary)',
          background: 'rgba(6,214,160,0.06)', marginBottom: 14,
        }}>
          {avisoRefresh}
        </ErrorBox>
      )}

      <TechHeader>
        <TechTitle>{crisis.titulo}</TechTitle>
        <Barra>
          {crisis.tipo === 'oportunidad' && (
            <TechChip style={{ color: '#06d6a0', borderColor: 'rgba(6,214,160,0.4)' }}>🎯 oportunidad</TechChip>
          )}
          <TechChip tone={crisis.severidad === 'alta' ? 'danger' : crisis.severidad === 'media' ? 'warn' : undefined}>
            gravedad {crisis.severidad}
          </TechChip>
          <TechChip>{ETIQUETA_ESTADO[crisis.estado] || crisis.estado}</TechChip>
          <TechChip>{crisis.senales} {crisis.senales === 1 ? 'señal' : 'señales'}</TechChip>
          <TechChip>empezó el {new Date(ventana.inicio).toLocaleDateString('es-AR')}</TechChip>
          {ultimoParte && (
            <TechChip>
              actualizado {new Date(ultimoParte).toLocaleString('es-AR', {
                day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
              })}
            </TechChip>
          )}
        </Barra>
        {crisis.resumen && <Resumen>{crisis.resumen}</Resumen>}
        <Barra>
          {kit && (
            <Link to={`/urgencia?id=${kit.id}`}>
              <TechButton as="span"><FaFolderOpen /> Abrir el kit de respuesta</TechButton>
            </Link>
          )}
          {crisis.estado === 'detectada' && (
            <TechButton onClick={() => cambiarCrisis('confirmada')} disabled={ocupado}>
              Confirmar que es real
            </TechButton>
          )}
          {abierta && crisis.estado !== 'contenida' && crisis.estado !== 'detectada' && (
            <TechButtonGhost onClick={() => cambiarCrisis('contenida')} disabled={ocupado}>
              Marcar como contenida
            </TechButtonGhost>
          )}
          {abierta && (
            <TechButtonGhost onClick={() => cambiarCrisis('resuelta')} disabled={ocupado}>
              Dar por cerrada
            </TechButtonGhost>
          )}
        </Barra>
      </TechHeader>

      {error && <ErrorBox>{error}</ErrorBox>}

      {/* El asistente de la crisis vive como burbuja flotante global
          (ChatCrisis en App.js), arriba de la del Asistente Arcana. */}

      {/* ---------- Desenlace sugerido ---------- */}
      {/* El sistema evalúa 2x/día si el caso se apaga o ya se ganó. Si sugiere
          bajar el estado y la crisis todavía está más caliente, se muestra acá
          con el botón para aplicarlo — cerrar sigue siendo decisión humana. */}
      {resolucion && resolucion.veredicto && resolucion.veredicto !== 'activa'
        && !(resolucion.veredicto === 'contenida' && crisis.estado === 'contenida') && abierta && (
        <Desenlace $ok={resolucion.a_favor}>
          <div className="cab">
            {resolucion.a_favor ? '🟢' : '🔵'} El sistema sugiere marcar esta crisis como{' '}
            <strong>{resolucion.veredicto}</strong>
          </div>
          {resolucion.desenlace && <p>{resolucion.desenlace}</p>}
          {resolucion.razon && <p className="razon">{resolucion.razon}</p>}
          {resolucion.dias_sin_cobertura >= 3 && (
            <p className="razon">Hace {resolucion.dias_sin_cobertura} días que no sale una nota del tema.</p>
          )}
          <Barra>
            {resolucion.veredicto === 'resuelta' ? (
              <TechButton onClick={() => cambiarCrisis('resuelta')} disabled={ocupado}>
                Dar por cerrada
              </TechButton>
            ) : (
              <>
                <TechButton onClick={() => cambiarCrisis('contenida')} disabled={ocupado}>
                  Marcar contenida
                </TechButton>
                <TechButtonGhost onClick={() => cambiarCrisis('resuelta')} disabled={ocupado}>
                  Dar por cerrada
                </TechButtonGhost>
              </>
            )}
          </Barra>
        </Desenlace>
      )}

      {/* ---------- Números de arriba ---------- */}
      <StatStrip>
        <StatTile>
          <StatLabel>Notas publicadas</StatLabel>
          <StatValue>{fallo('cobertura') ? '—' : (cobertura?.total ?? 0)}</StatValue>
          <StatSub>
            {fallo('cobertura') ? 'no se pudo contar'
              : cobertura?.pico ? `pico el ${diaCorto(cobertura.pico.fecha)} (${cobertura.pico.n})`
                : 'desde que empezó'}
          </StatSub>
        </StatTile>

        <StatTile>
          <StatLabel>Medios que lo cubrieron</StatLabel>
          <StatValue>{fallo('cobertura') ? '—' : (cobertura?.medios?.length ?? 0)}</StatValue>
          <StatSub>{silencio.length ? `${silencio.length} activos no lo tocaron` : 'sobre los medios seguidos'}</StatSub>
        </StatTile>

        <StatTile>
          <StatLabel>Imagen desde que empezó</StatLabel>
          <StatValue style={{ color: delta == null ? undefined : delta < 0 ? C.alerta : C.ok }}>
            {fallo('reputacion') || delta == null ? '—' : `${delta > 0 ? '+' : ''}${delta.toFixed(1)}`}
          </StatValue>
          <StatSub>
            {fallo('reputacion') ? 'no se pudo medir'
              : delta == null ? 'faltan días para comparar'
                : reputacion.dias_despues < 3
                  ? `${reputacion.score_antes?.toFixed(1)} → ${reputacion.score_despues?.toFixed(1)} · pocos días aún`
                  : `${reputacion.score_antes?.toFixed(1)} → ${reputacion.score_despues?.toFixed(1)}`}
          </StatSub>
        </StatTile>

        <StatTile>
          <StatLabel>Plan de acción</StatLabel>
          <StatValue>{progreso ? `${progreso.pct}%` : '—'}</StatValue>
          <StatSub>
            {progreso ? `${progreso.hechas} de ${progreso.total} hechas` : 'sin acciones cargadas'}
          </StatSub>
        </StatTile>
      </StatStrip>

      {progreso?.total > 0 && (
        <TechBar style={{ marginBottom: '1.2rem' }}>
          <TechBarFill style={{ width: `${progreso.pct}%`, background: C.ok }} />
        </TechBar>
      )}

      <Grilla>
        {/* ---------- Plan de acción ---------- */}
        <TechPanel style={{ gridColumn: '1 / -1' }}>
          <PanelHeader>
            <PanelTitle>Qué hay que hacer</PanelTitle>
            {progreso && (
              <Medio>{progreso.en_curso} en curso · {progreso.pendientes} sin empezar</Medio>
            )}
          </PanelHeader>
          <Leyenda>
            El círculo mueve cada acción: sin empezar → en curso → hecha. Tocá el texto para
            abrirla: adentro tenés el paso a paso, dónde anotar los contactos y teléfonos, y la
            consulta a la IA. Las que dicen "del plan" salieron del kit.
          </Leyenda>

          {acciones.length === 0 ? (
            <Empty>
              Todavía no hay acciones. {kit
                ? 'El kit tiene un plan pero no se pudo desglosar; cargá las acciones a mano.'
                : 'Armá el kit de respuesta o cargá las acciones a mano.'}
            </Empty>
          ) : (
            acciones.map((a) => (
              <AccionCrisis
                key={a.id}
                accion={a}
                ocupado={ocupado}
                onCiclar={ciclar}
                onBorrar={(accionId) => conBloqueo(() => borrarAccion(id, accionId))}
                onAbrir={setAccionAbierta}
              />
            ))
          )}

          <Agregar>
            <TechInput
              placeholder="Agregar algo que haya que hacer…"
              value={nueva}
              onChange={(e) => setNueva(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') agregar(); }}
            />
            <TechSelect value={prioridadNueva} onChange={(e) => setPrioridadNueva(e.target.value)}>
              <option value="alta">Urgente</option>
              <option value="media">Normal</option>
              <option value="baja">Cuando se pueda</option>
            </TechSelect>
            <TechButton onClick={agregar} disabled={ocupado || nueva.trim().length < 3}>
              <FaPlus /> Agregar
            </TechButton>
          </Agregar>
        </TechPanel>

        {/* ---------- Cobertura ---------- */}
        <TechPanel>
          <PanelHeader><PanelTitle>Cuánto se habló, día por día</PanelTitle></PanelHeader>
          {fallo('cobertura') ? (
            <ErrorBox>No se pudo medir la cobertura de prensa.</ErrorBox>
          ) : !cobertura?.serie?.length ? (
            <Empty>Todavía no hay notas publicadas sobre esto.</Empty>
          ) : (
            <>
              {cobertura.lectura && (
                <Lectura $color={(FASE[cobertura.lectura.fase] || {}).color}>
                  {cobertura.lectura.texto}
                </Lectura>
              )}
              <Leyenda>
                La línea punteada marca el día de la primera nota publicada.
              </Leyenda>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={cobertura.serie} margin={{ top: 6, right: 8, left: -22, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="fecha" tickFormatter={diaCorto} tick={ejeStyle} />
                  <YAxis allowDecimals={false} tick={ejeStyle} />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    labelFormatter={diaCorto}
                    formatter={(v) => [v, 'notas']}
                  />
                  <ReferenceLine
                    x={ventana.inicio.slice(0, 10)}
                    stroke={C.alerta} strokeDasharray="4 4"
                  />
                  <Area type="monotone" dataKey="notas" stroke={C.dato} fill={C.dato} fillOpacity={0.18} />
                </AreaChart>
              </ResponsiveContainer>

              {cobertura.lectura && (
                <Cifras>
                  <Cifra $color={(FASE[cobertura.lectura.fase] || {}).color}>
                    <span className="n">{(FASE[cobertura.lectura.fase] || {}).texto}</span>
                    <span className="r">cómo viene</span>
                  </Cifra>
                  <Cifra>
                    <span className="n">{cobertura.lectura.ultimos7}</span>
                    <span className="r">esta semana</span>
                  </Cifra>
                  <Cifra>
                    <span className="n">{cobertura.lectura.previos7}</span>
                    <span className="r">la anterior</span>
                  </Cifra>
                  <Cifra>
                    <span className="n">
                      {cobertura.lectura.dias_sin_notas === 0
                        ? 'hoy'
                        : `${cobertura.lectura.dias_sin_notas}d`}
                    </span>
                    <span className="r">última nota</span>
                  </Cifra>
                  <Cifra>
                    <span className="n">{cobertura.medios?.length || 0}</span>
                    <span className="r">medios</span>
                  </Cifra>
                </Cifras>
              )}
            </>
          )}
        </TechPanel>

        {/* ---------- Reputación ---------- */}
        <TechPanel>
          <PanelHeader><PanelTitle>Cómo viene la imagen</PanelTitle></PanelHeader>
          {fallo('reputacion') ? (
            <ErrorBox>No se pudo medir el impacto en la imagen.</ErrorBox>
          ) : !reputacion?.serie?.length ? (
            <Empty>No hay mediciones de imagen en este período.</Empty>
          ) : (
            <>
              {reputacion.lectura && <Lectura>{reputacion.lectura.texto}</Lectura>}
              <Leyenda>
                Puntaje de imagen (celeste) y menciones negativas por día (roja).
                La punteada marca el inicio de la situación.
                {reputacion.lectura?.descartados > 0 && (
                  <> La línea celeste se corta en los días con menos de{' '}
                  {reputacion.min_menciones} menciones: ahí no hubo muestra suficiente
                  para medir, y dibujar un puntaje sería inventar una caída.</>
                )}
              </Leyenda>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={reputacion.serie} margin={{ top: 6, right: 8, left: -22, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="fecha" tickFormatter={diaCorto} tick={ejeStyle} />
                  <YAxis yAxisId="i" tick={ejeStyle} />
                  <YAxis yAxisId="n" orientation="right" tick={ejeStyle} allowDecimals={false} />
                  <Tooltip contentStyle={tooltipStyle} labelFormatter={diaCorto} />
                  <ReferenceLine yAxisId="i" x={reputacion.corte} stroke={C.alerta} strokeDasharray="4 4" />
                  {/* score_medido es null donde no hubo muestra: con
                      connectNulls en false, la línea se corta ahí. */}
                  <Line yAxisId="i" type="monotone" dataKey="score_medido" stroke={C.dato}
                        strokeWidth={2} dot={{ r: 2 }} connectNulls={false} name="imagen" />
                  {/* Las negativas explican el movimiento del puntaje: si cae, acá
                      se ve si fue por más ataques o por menos volumen general. */}
                  <Line yAxisId="n" type="monotone" dataKey="negativas" stroke={C.alerta}
                        strokeWidth={1.5} dot={false} strokeOpacity={0.75} name="negativas" />
                </LineChart>
              </ResponsiveContainer>

              {reputacion.lectura && (
                <Cifras>
                  <Cifra $color={reputacion.lectura.var7 < 0 ? C.alerta : C.ok}>
                    <span className="n">
                      {reputacion.lectura.actual != null ? reputacion.lectura.actual.toFixed(1) : '—'}
                    </span>
                    <span className="r">imagen hoy</span>
                  </Cifra>
                  <Cifra $color={reputacion.lectura.var7 == null ? undefined
                    : reputacion.lectura.var7 < 0 ? C.alerta : C.ok}>
                    <span className="n">
                      {reputacion.lectura.var7 != null
                        ? `${reputacion.lectura.var7 > 0 ? '+' : ''}${reputacion.lectura.var7.toFixed(1)}`
                        : '—'}
                    </span>
                    <span className="r">
                      {reputacion.lectura.var7 != null
                        ? `vs. ${diaCorto(reputacion.lectura.var7_desde)}`
                        : 'sin comparación a 7 días'}
                    </span>
                  </Cifra>
                  {reputacion.lectura.minimo != null && (
                    <Cifra $color={C.alerta}>
                      <span className="n">{reputacion.lectura.minimo.toFixed(1)}</span>
                      <span className="r">
                        piso {reputacion.lectura.fecha_minimo
                          ? `· ${diaCorto(reputacion.lectura.fecha_minimo)}` : ''}
                      </span>
                    </Cifra>
                  )}
                  <Cifra>
                    <span className="n">{reputacion.lectura.dias_medidos}</span>
                    <span className="r">
                      días medidos
                      {reputacion.lectura.descartados > 0
                        ? ` · ${reputacion.lectura.descartados} sin muestra` : ''}
                    </span>
                  </Cifra>
                </Cifras>
              )}
            </>
          )}
        </TechPanel>

        {/* ---------- Medios ---------- */}
        <TechPanel>
          <PanelHeader><PanelTitle>Quién lo publicó</PanelTitle></PanelHeader>
          {fallo('cobertura') ? (
            <ErrorBox>No se pudo cargar el detalle por medio.</ErrorBox>
          ) : !cobertura?.medios?.length ? (
            <Empty>Ningún medio lo publicó todavía.</Empty>
          ) : (
            cobertura.medios.map((m) => (
              <FilaClic
                key={m.medio}
                role="button"
                tabIndex={0}
                onClick={() => setMedioAbierto(m.medio)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setMedioAbierto(m.medio); } }}
                title={`Ver lo que publicó ${m.medio}`}
              >
                <Texto>
                  <Titulo>{m.medio}</Titulo>
                  <Sub>última nota el {new Date(m.ultima).toLocaleDateString('es-AR')}</Sub>
                </Texto>
                <TechChip>{m.n} {m.n === 1 ? 'nota' : 'notas'}</TechChip>
              </FilaClic>
            ))
          )}
          {silencio.length > 0 && (
            <>
              <Leyenda style={{ marginTop: 14 }}>
                <FaBullhorn /> Estos medios publicaron otras cosas estos días pero no tocaron el tema.
                Son a quién llamar — o una señal de con quién están.
              </Leyenda>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {silencio.map((m) => <TechChip key={m}>{m}</TechChip>)}
              </div>
            </>
          )}
        </TechPanel>

        {/* La lista "Lo que se publicó" se ocultó: las notas de cada medio se
            ven tocándolo en "Quién lo publicó". */}

        {/* ---------- En redes ---------- */}
        {redes.length > 0 && (
          <TechPanel>
            <PanelHeader>
              <PanelTitle>En redes sociales</PanelTitle>
              {!redes[0].del_tema && <TechChip tone="warn">clima general</TechChip>}
            </PanelHeader>
            <Leyenda>
              Publicaciones del monitoreo de redes, con su reacción. Tocá para ver el posteo.
            </Leyenda>
            {redes.map((p, i) => (
              <Nota
                key={p.url || i}
                href={p.url || undefined}
                target={p.url ? '_blank' : undefined}
                rel="noopener noreferrer"
                onClick={(e) => { if (!p.url) e.preventDefault(); }}
              >
                <Titulo className="t">{p.caption || '(sin texto)'}</Titulo>
                <Sub>
                  {p.pos != null && <span style={{ color: '#06d6a0' }}>{p.pos} a favor</span>}
                  {p.neg != null && <> · <span style={{ color: '#ef476f' }}>{p.neg} en contra</span></>}
                  {p.likes != null && <> · {p.likes} 👍</>}
                  {p.comentarios != null && <> · {p.comentarios} coment.</>}
                </Sub>
              </Nota>
            ))}
          </TechPanel>
        )}

        {/* ---------- Reclamos de vecinos ---------- */}
        {reclamos.length > 0 && (
          <TechPanel>
            <PanelHeader><PanelTitle>Lo que llegó de los vecinos</PanelTitle></PanelHeader>
            {reclamos.map((r) => (
              <Fila key={r.id}>
                <Texto>
                  <Titulo>{r.titulo}</Titulo>
                  <Sub>{new Date(r.created_at).toLocaleDateString('es-AR')} · {r.estado}</Sub>
                </Texto>
              </Fila>
            ))}
          </TechPanel>
        )}

        {/* ---------- Datos accionables del kit ---------- */}
        {/* En vez del índice de títulos: el contenido útil (qué decir, qué te
            van a preguntar, qué vigilar, qué no hacer) directo a la vista. */}
        <KitFuncional kit={kit} />

        {/* ---------- Evolución (partes 2x/día) ---------- */}
        <TechPanel style={{ gridColumn: '1 / -1' }}>
          <PanelHeader><PanelTitle>Cómo evoluciona, parte a parte</PanelTitle></PanelHeader>
          {evolucion.length === 0 ? (
            <Empty>
              Todavía no hay partes. Mientras la crisis esté abierta, el sistema actualiza los
              datos y toma una foto dos veces por día (8 y 20 h) — acá vas a ver cómo viene.
            </Empty>
          ) : (
            <>
              <Leyenda>
                Cada punto es un parte automático. Notas totales acumuladas (celeste) e imagen (línea clara).
              </Leyenda>
              <ResponsiveContainer width="100%" height={190}>
                <LineChart
                  data={evolucion.map((e) => ({
                    t: new Date(e.fecha).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit' }),
                    notas: e.notas_total, imagen: e.score,
                  }))}
                  margin={{ top: 6, right: 8, left: -22, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="t" tick={ejeStyle} />
                  <YAxis yAxisId="n" tick={ejeStyle} allowDecimals={false} />
                  <YAxis yAxisId="i" orientation="right" tick={ejeStyle} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Line yAxisId="n" type="monotone" dataKey="notas" stroke={C.dato} strokeWidth={2} dot={{ r: 2 }} name="notas" />
                  <Line yAxisId="i" type="monotone" dataKey="imagen" stroke="#cbd5e1" strokeWidth={1.5} dot={false} name="imagen" connectNulls />
                </LineChart>
              </ResponsiveContainer>
              <Cifras>
                <Cifra><span className="n">{evolucion.length}</span><span className="r">partes</span></Cifra>
                <Cifra>
                  <span className="n">{evolucion[evolucion.length - 1].notas_dia ?? '—'}</span>
                  <span className="r">notas en el último</span>
                </Cifra>
              </Cifras>
            </>
          )}
        </TechPanel>

        {/* ---------- Humor social (reemplaza la vieja línea de tiempo) ---------- */}
        <HumorSocial crisisId={id} />
      </Grilla>

      {cobertura?.total > 0 && (
        <TechSubtitle style={{ marginTop: '1.2rem', fontSize: '0.78rem' }}>
          <FaNewspaper /> Tocá un medio en "Quién lo publicó" para ver todo lo que sacó.
          Las notas se cuentan buscando {panel.keywords?.length} palabras vigiladas
          en los medios que sigue la plataforma, desde el {new Date(ventana.desde).toLocaleDateString('es-AR')}.
          "Empezó el" es la fecha de la primera nota publicada, no la de cuando el sistema se enteró.
        </TechSubtitle>
      )}

      {medioAbierto && (
        <NotasMedioModal
          medio={medioAbierto}
          notas={notasDelMedio}
          total={(cobertura?.medios || []).find((m) => m.medio === medioAbierto)?.n}
          onCerrar={() => setMedioAbierto(null)}
        />
      )}

      {accionAbierta && (
        <AccionModal
          crisisId={id}
          accion={accionAbierta}
          onCerrar={() => setAccionAbierta(null)}
          onCambio={cargar}
        />
      )}
    </TechShell>
  );
}
